import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import Footer from '../components/Footer';
import '../styles/UuidGeneratorPage.css';

function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const COUNT_OPTIONS = [1, 5, 10, 25, 50];
const FORMAT_OPTIONS = [
  { label: 'Standard', value: 'standard' },
  { label: 'No Dashes', value: 'nodash' },
  { label: 'UPPERCASE', value: 'upper' },
];

function applyFormat(uuid, format) {
  if (format === 'nodash') return uuid.replace(/-/g, '');
  if (format === 'upper') return uuid.toUpperCase();
  return uuid;
}

export default function UuidGeneratorPage() {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [count, setCount] = useState(5);
  const [format, setFormat] = useState('standard');
  const [uuids, setUuids] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [copyAllMsg, setCopyAllMsg] = useState('Copy All');

  const generate = useCallback(() => {
    const newUuids = Array.from({ length: count }, () => generateUUID());
    setUuids(newUuids);
    setCopiedIdx(null);
  }, [count]);

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = (idx) => {
    const text = applyFormat(uuids[idx], format);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const handleCopyAll = () => {
    const text = uuids.map((u) => applyFormat(u, format)).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopyAllMsg('Copied!');
      setTimeout(() => setCopyAllMsg('Copy All'), 2000);
    });
  };

  const handleClear = () => setUuids([]);

  return (
    <div
      className={`container ${darkMode ? 'dark-theme' : ''}`}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}
    >
      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: '#1976d2',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
      >
        {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
      </button>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: darkMode ? '#42a5f5' : '#1976d2', textAlign: { xs: 'left', sm: 'center' } }}>
          🔑 UUID Generator
        </Typography>
        <Box sx={{ width: { sm: '100px' } }} />
      </Box>

      {/* Controls */}
      <div className="uuid-controls">
        {/* Count selector */}
        <div>
          <div className="uuid-control-label">Count</div>
          <div className="count-selector">
            {COUNT_OPTIONS.map((n) => (
              <button
                key={n}
                className={`count-btn${count === n ? ' active' : ''}`}
                onClick={() => setCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Format selector */}
        <div>
          <div className="uuid-control-label">Format</div>
          <div className="format-selector">
            {FORMAT_OPTIONS.map((f) => (
              <button
                key={f.value}
                className={`format-btn${format === f.value ? ' active' : ''}`}
                onClick={() => setFormat(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Generate button */}
        <div style={{ alignSelf: 'flex-end' }}>
          <button className="uuid-generate-btn" onClick={generate}>
            ⚡ Generate
          </button>
        </div>
      </div>

      {/* Action bar */}
      {uuids.length > 0 && (
        <div className="uuid-action-bar">
          <span className="uuid-count-badge">{uuids.length} UUID{uuids.length !== 1 ? 's' : ''}</span>
          <button className="uuid-action-btn" onClick={handleCopyAll}>{copyAllMsg}</button>
          <button className="uuid-action-btn danger" onClick={handleClear}>Clear</button>
        </div>
      )}

      {/* UUID list */}
      {uuids.length > 0 && (
        <div className="uuid-list">
          {uuids.map((uuid, idx) => (
            <div key={idx} className={`uuid-row${idx % 2 === 0 ? ' even' : ' odd'}`}>
              <span className="uuid-text">{applyFormat(uuid, format)}</span>
              <button
                className={`uuid-copy-btn${copiedIdx === idx ? ' copied' : ''}`}
                onClick={() => handleCopy(idx)}
              >
                {copiedIdx === idx ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>
          ))}
        </div>
      )}

      {uuids.length === 0 && (
        <div className="uuid-empty">
          <Typography variant="body1" sx={{ color: darkMode ? '#90caf9' : '#1976d2', textAlign: 'center', mt: 4 }}>
            Click "Generate" to create UUIDs.
          </Typography>
        </div>
      )}

      <Footer />
    </div>
  );
}
